import { useEffect, useMemo, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { getChecklist, subscribeChecklist } from "../data/checklist";
import { currentEmail, loadProgress, saveProgress } from "../lib/progress";

/** Step 7 — essential first-day checklist. Every item must be either checked
 *  done or explicitly deferred ("not completed yet") to finish the journey —
 *  a real item you can't yet act on (device not delivered, MFA pending IT) no
 *  longer blocks the whole journey; it's recorded for IT/manager follow-up. */
export function ChecklistScreen({ lang, onNext, onBack }: { lang: Lang; onNext: () => void; onBack: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const email = useMemo(() => currentEmail(), []);
  const saved = useMemo(() => (email ? loadProgress(email) : null), [email]);
  const [checked, setChecked] = useState<Set<string>>(() => new Set(saved?.checkedItems || []));
  const [pending, setPending] = useState<Set<string>>(() => new Set(saved?.pendingItems || []));
  const [checklist, setChecklist] = useState(() => getChecklist());
  useEffect(() => subscribeChecklist(() => setChecklist(getChecklist())), []);

  useEffect(() => {
    if (!email) return;
    saveProgress(email, { checkedItems: [...checked], pendingItems: [...pending] });
  }, [checked, pending, email]);

  // Each item is one of three states: neutral, done, or deferred — the two
  // buttons below are mutually exclusive both ways (marking one clears the
  // other), and clicking an already-active button returns to neutral.
  const markDone = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setPending((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  const markPending = (id: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setChecked((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addressed = checked.size + pending.size;
  const allAddressed = addressed === checklist.length;
  const hasPending = pending.size > 0;

  return (
    <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="glass-panel step-checklist">
        <div className="kd-step-header">
          <h1>{t("قائمة المراجعة الأساسية", "Essential Checklist")}</h1>
          <p>
            {t(
              "أكّد كل عنصر عند إتمامه. إذا تعذّر عليك إنجاز عنصر الآن، ضعه كـ«لم يكتمل بعد» بدلاً من التوقف هنا، سيصل تلقائياً لفريق تقنية المعلومات ومديرك المباشر للمتابعة.",
              "Confirm each item as you complete it. If you can't finish one right now, mark it \"Not completed yet\" instead of getting stuck here, it will automatically reach IT and your manager for follow-up.",
            )}
          </p>
        </div>

        <div className="kd-checklist">
          {checklist.map((item) => {
            const isChecked = checked.has(item.id);
            const isPending = pending.has(item.id);
            return (
              <div key={item.id} className={`kd-check-item${isChecked ? " checked" : ""}${isPending ? " kd-check-pending" : ""}`}>
                <span className="kd-check-label">{lang === "ar" ? item.ar : item.en}</span>
                <div className="kd-check-actions">
                  <button type="button" className={`kd-check-done-btn${isChecked ? " is-active" : ""}`} onClick={() => markDone(item.id)} aria-pressed={isChecked}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{t("أتممت المهمة", "Completed")}</span>
                  </button>
                  <button type="button" className={`kd-check-pending-btn${isPending ? " is-active" : ""}`} onClick={() => markPending(item.id)} aria-pressed={isPending}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    <span>{t("لم يكتمل بعد", "Not completed yet")}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="kd-check-progress">
          <div className="kd-check-bar">
            <div className="kd-check-fill" style={{ width: `${Math.round((addressed / checklist.length) * 100)}%` }} />
          </div>
          <span className="kd-check-count">{addressed}/{checklist.length}</span>
        </div>

        <div className="kd-sys-nav">
          {onBack && (
            <button className="kd-btn-secondary" onClick={onBack}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={lang === "ar" ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
              </svg>
              <span>{t("السابق، خريطة الأنظمة", "Back, Systems Map")}</span>
            </button>
          )}
          <button className="kd-btn-primary" onClick={onNext} disabled={!allAddressed}>
            <span>
              {!allAddressed
                ? t("يُرجى تأكيد أو تأجيل جميع العناصر أولاً", "Please confirm or defer all items first")
                : hasPending
                  ? t(`إتمام الرحلة (${pending.size} بند معلّق)`, `Complete the journey (${pending.size} pending)`)
                  : t("إتمام الرحلة", "Complete the journey")}
            </span>
            {allAddressed && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={lang === "ar" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
              </svg>
            )}
          </button>
        </div>

        <div className="kd-inline-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>
            {t(
              "البنود المؤجَّلة تُرسَل تلقائياً لفريق تقنية المعلومات ومديرك المباشر عند إتمام الرحلة، ولا تحتاج تذكيراً إضافياً منك.",
              "Deferred items are automatically sent to the IT team and your direct manager when you complete the journey, no extra reminder needed from you.",
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
